/**
 * Cart Confirmation Panel Integration
 * Intercepts add-to-cart actions and displays confirmation panel
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const confirmationPanel = document.querySelector('cart-confirmation-panel');
    if (!confirmationPanel) {
      console.warn('Cart confirmation panel not found');
      return;
    }

    // Always use theme's existing events (ProductForm component handles all form submissions)
    // This ensures we don't duplicate cart additions
    document.addEventListener('variant:add', handleThemeCartAdd);
    document.addEventListener('cart:change', handleCartChange);

    // Listen for custom cart add events (legacy support)
    document.addEventListener('cart:item-added', handleCartItemAdded);
  }

  /**
   * Intercept product form submissions
   */
  function interceptProductForms() {
    // Find all product forms
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');

    productForms.forEach(form => {
      // Check if form already has our listener to prevent duplicate handlers
      if (form.dataset.cartConfirmationBound) {
        return;
      }
      form.dataset.cartConfirmationBound = 'true';

      form.addEventListener('submit', async function(e) {
        e.preventDefault();
        e.stopImmediatePropagation(); // Prevent other listeners from firing

        const formData = new FormData(form);
        const submitButton = form.querySelector('[type="submit"]');

        // Prevent double submission
        if (submitButton) {
          if (submitButton.disabled) {
            return; // Already processing
          }
          submitButton.disabled = true;
        }

        // Show loading spinner immediately
        const confirmationPanel = document.querySelector('cart-confirmation-panel');
        if (confirmationPanel && typeof confirmationPanel.showLoading === 'function') {
          confirmationPanel.showLoading();
        }

        try {
          // Submit to Shopify cart API
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            throw new Error('Failed to add item to cart');
          }

          const item = await response.json();

          // Item successfully added - show panel with content
          showConfirmationPanel(item);

          // Update cart count in header asynchronously (non-blocking)
          updateCartCount();

          // Dispatch custom event for other scripts
          document.dispatchEvent(new CustomEvent('cart:item-added', {
            detail: { item }
          }));

        } catch (error) {
          console.error('Error adding item to cart:', error);

          // Hide loading on error
          if (confirmationPanel && typeof confirmationPanel.hideLoading === 'function') {
            confirmationPanel.hideLoading();
          }

          alert('There was an error adding the item to your cart. Please try again.');
        } finally {
          // Re-enable submit button after a short delay to prevent rapid clicks
          if (submitButton) {
            setTimeout(() => {
              submitButton.disabled = false;
            }, 500);
          }
        }
      });
    });
  }

  /**
   * Handle theme's variant:add event (ProductForm component)
   */
  function handleThemeCartAdd(event) {
    if (event.detail && event.detail.items && event.detail.items.length > 0) {
      // Get the first item that was added
      const item = event.detail.items[0];

      // Show panel with product data (this will hide the loading spinner)
      showConfirmationPanel(item);
    }
  }

  /**
   * Handle theme's cart:change event
   */
  function handleCartChange(event) {
    // Only handle variant:add events to avoid interfering with cart removal/updates
    if (!event.detail || event.detail.baseEvent !== 'variant:add') {
      return; // Exit immediately for non-add events (like line-item:change)
    }

    // Update cart count from the cart data
    if (event.detail.cart) {
      updateCartCountFromData(event.detail.cart);
    }
  }

  /**
   * Handle custom cart item added event
   */
  function handleCartItemAdded(event) {
    if (event.detail && event.detail.item) {
      showConfirmationPanel(event.detail.item);
    }
  }

  /**
   * Show confirmation panel with product data
   * Optimized for instant display with minimal processing
   */
  function showConfirmationPanel(item) {
    const confirmationPanel = document.querySelector('cart-confirmation-panel');
    if (!confirmationPanel) return;

    // Pre-format all data synchronously for instant display
    const productData = {
      image: item.featured_image?.url || item.image,
      vendor: item.vendor,
      name: item.product_title,
      variantOptions: formatVariantOptions(item),
      quantity: item.quantity,
      priceHtml: formatPriceHtml(item.final_price)
    };

    // Show panel immediately with pre-formatted data
    confirmationPanel.show(productData);
  }

  /**
   * Format variant options as array of {name, value} objects
   * Returns array for dropdown-style display
   */
  function formatVariantOptions(item) {
    const options = [];
    const locale = document.documentElement.lang || 'en';

    // Check if item has options_with_values (preferred method)
    if (item.options_with_values && item.options_with_values.length > 0) {
      item.options_with_values.forEach(option => {
        if (option.value && option.value !== 'Default Title') {
          // Translate common option names to Arabic if needed
          let optionName = option.name;
          if (locale === 'ar') {
            const translations = {
              'Size': 'المقاس',
              'Color': 'اللون',
              'Material': 'المادة',
              'Style': 'النمط'
            };
            optionName = translations[option.name] || option.name;
          }

          options.push({
            name: optionName,
            value: option.value
          });
        }
      });
    }
    // Fallback to variant_options if available
    else if (item.variant_options && item.variant_title !== 'Default Title') {
      const variantTitles = item.variant_title.split(' / ');
      item.variant_options.forEach((optionName, index) => {
        if (variantTitles[index] && variantTitles[index] !== 'Default Title') {
          // Translate common option names
          let translatedName = optionName;
          if (locale === 'ar') {
            const translations = {
              'Size': 'المقاس',
              'Color': 'اللون',
              'Material': 'المادة',
              'Style': 'النمط'
            };
            translatedName = translations[optionName] || optionName;
          }

          options.push({
            name: translatedName,
            value: variantTitles[index]
          });
        }
      });
    }

    return options;
  }

  /**
   * Format price as HTML matching the theme's price-list snippet
   * Applies 1.15x multiplier for tax (15% VAT)
   * Returns proper price-list structure for currency converter compatibility
   */
  function formatPriceHtml(priceInCents) {
    // Apply 15% tax like in price-list.liquid
    const priceWithTax = priceInCents * 1.15;
    const price = priceWithTax / 100;

    // Get currency settings
    const currencyCode = window.Shopify?.currency?.active || 'SAR';
    const locale = document.documentElement.lang || 'en';

    // Format money - use Shopify's money format which is compatible with currency converters
    let formattedPrice;
    if (window.Shopify && window.Shopify.formatMoney) {
      formattedPrice = window.Shopify.formatMoney(priceWithTax, window.theme?.moneyFormat);
    } else {
      // Fallback formatting that mimics Shopify's money format structure
      // This format is what currency converter apps expect to find and transform
      const priceFormatted = price.toFixed(2);
      formattedPrice = `<span class="money">${priceFormatted} ${currencyCode}</span>`;
    }

    // Return formatted HTML with proper structure matching price-list.liquid
    // This structure allows currency converter apps to find and transform the price
    const label = locale === 'ar' ? 'سعر البيع' : 'Sale price';
    return `<sale-price class="text-subdued">
      <span class="sr-only">${label}</span>${formattedPrice}
    </sale-price>`;
  }

  /**
   * Update cart count from cart data object (no fetch needed)
   */
  function updateCartCountFromData(cart) {
    if (!cart || typeof cart.item_count === 'undefined') return;

    // Update all cart count elements
    const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, #cart-count');
    cartCountElements.forEach(element => {
      element.textContent = cart.item_count;

      // Show badge if hidden
      if (cart.item_count > 0) {
        element.style.display = '';
        element.setAttribute('aria-label', `${cart.item_count} items in cart`);
      }
    });
  }

  /**
   * Update cart count in header (fetch from API)
   */
  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      updateCartCountFromData(cart);

      // Note: Cart drawer refresh is handled by theme's cart:change event
      // No need to manually refresh here to avoid conflicts/slowdowns

    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Alternative: Use AJAX cart if theme already has it
   * This function can be used as a fallback
   */
  function hookIntoExistingAjaxCart() {
    // Check if theme uses a global cart object
    if (window.theme && window.theme.cart) {
      const originalAddItem = window.theme.cart.addItem;

      if (originalAddItem) {
        window.theme.cart.addItem = function(...args) {
          const result = originalAddItem.apply(this, args);

          // If addItem returns a promise, hook into it
          if (result && typeof result.then === 'function') {
            result.then(item => {
              showConfirmationPanel(item);
            });
          }

          return result;
        };
      }
    }
  }

  // Try to hook into existing cart system as well
  hookIntoExistingAjaxCart();

})();
